import { DataFrame, Method, Status } from "lavva.exalushome/build/js/DataFrame";
import { IDevice } from "lavva.exalushome/build/js/Services/Devices/IDevice";
import { ResponseResult } from "lavva.exalushome/build/js/Services/FieldChangeResult";
import { GetChannelParamsRequest } from "../Common/DataRequest";
import { OnOffSwitchService, SwitchConfigurationErrorCode } from "../Common/OnOffSwitchConfigurations/OnOffSwitchService";
import { IRdp21ConfigService } from "./IRdp21ConfigService";
import { InputOutputDimmerParameters, LightTransitionTime, MaxLightLevel, MinLightLevel } from "./Rdp21ConfigParameters";

export class Rdp21ConfigService extends OnOffSwitchService implements IRdp21ConfigService {

    public static readonly ServiceName: string = "Rdp21ConfigService";

    constructor() {
        super();
    }

    GetServiceName(): string {
        return Rdp21ConfigService.ServiceName;
    }

    async GetDimmerInputOutputParamAsync(device: IDevice, channel: number): Promise<ResponseResult<SwitchConfigurationErrorCode> | InputOutputDimmerParameters> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.InvalidChannelNumber, `Cannot get input/output parameters - invalid channel number, channel: ${channel}`);

            const result = await this._connection?.SendAndWaitForResponseAsync<InputOutputDimmerParameters[]>(new GetDimmerInputOutputParamRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get input/output parameters - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get input/output parameters - controller responded with error code: ${result.Status}.`);

            if(result.Data == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.NoData, `Cannot get input/output parameters - no data in response.`);

            const dataResult = result.Data.first();
            const returnObj = new InputOutputDimmerParameters();
            returnObj.Channel = dataResult.Channel;
            returnObj.InputModeDimmer = dataResult.InputModeDimmer;

            return returnObj;
        } catch (error) {
            return new ResponseResult(SwitchConfigurationErrorCode.OtherError, `Cannot get input/output parameters - unknown error.`);
        }
    }

    async SetDimmerInputOutputParamAsync(device: IDevice, params: InputOutputDimmerParameters): Promise<Status> {
        try {
            const request = new InputOutputDimmerParametersFrame();
            request.DeviceGuid = device.Guid;
            request.Channel = params.Channel;
            request.InputModeDimmer = params.InputModeDimmer;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetDimmerInputOutputParamRequest(request), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set input/output parameters - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set input/output parameters, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            return Status.FatalError;
        }
    }

    async GetBrighteningTimeAsync(device: IDevice, channel: number): Promise<ResponseResult<SwitchConfigurationErrorCode> | LightTransitionTime> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.InvalidChannelNumber, `Cannot get brightening time - invalid channel number, channel: ${channel}`);

            const result = await this._connection?.SendAndWaitForResponseAsync<DimmerSettingsParameters>(new GetBrighteningTimeRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get brightening time - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get brightening time - controller responded with error code: ${result.Status}.`);

            if(result.Data == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.NoData, `Cannot get brightening time - no data in response.`);

            return result.Data.State as LightTransitionTime;
        } catch (error) {
            return new ResponseResult(SwitchConfigurationErrorCode.OtherError, `Cannot get brightening time - unknown error.`);
        }
    }

    async SetBrighteningTimeAsync(device: IDevice, channel: number, time: LightTransitionTime): Promise<Status> {
        try {
            const request = new DimmerSettingsParameters();
            request.DeviceGuid = device.Guid;
            request.Channel = channel;
            request.State = time;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetBrighteningTimeRequest(request), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set brightening time - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set brightening time, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            return Status.FatalError;
        }
    }

    async GetDimmingTimeAsync(device: IDevice, channel: number): Promise<ResponseResult<SwitchConfigurationErrorCode> | LightTransitionTime> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.InvalidChannelNumber, `Cannot get dimming time - invalid channel number, channel: ${channel}`);

            const result = await this._connection?.SendAndWaitForResponseAsync<DimmerSettingsParameters>(new GetDimmingTimeRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get dimming time - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get dimming time - controller responded with error code: ${result.Status}.`);

            if(result.Data == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.NoData, `Cannot get dimming time - no data in response.`);

            return result.Data.State as LightTransitionTime;
        } catch (error) {
            return new ResponseResult(SwitchConfigurationErrorCode.OtherError, `Cannot get dimming time - unknown error.`);
        }
    }

    async SetDimmingTimeAsync(device: IDevice, channel: number, time: LightTransitionTime): Promise<Status> {
        try {
            const request = new DimmerSettingsParameters();
            request.DeviceGuid = device.Guid;
            request.Channel = channel;
            request.State = time;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetDimmingTimeRequest(request), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set dimming time - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set dimming time, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            return Status.FatalError;
        }
    }

    async GetMinLevelAsync(device: IDevice, channel: number): Promise<ResponseResult<SwitchConfigurationErrorCode> | MinLightLevel> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.InvalidChannelNumber, `Cannot get mimimum light level - invalid channel number, channel: ${channel}`);

            const result = await this._connection?.SendAndWaitForResponseAsync<DimmerSettingsParameters>(new GetMinLightLevelRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get mimimum light level - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get mimimum light level - controller responded with error code: ${result.Status}.`);

            if(result.Data == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.NoData, `Cannot get mimimum light level - no data in response.`);

            return result.Data.State as MinLightLevel;
        } catch (error) {
            return new ResponseResult(SwitchConfigurationErrorCode.OtherError, `Cannot get mimimum light level - unknown error.`);
        }
    }

    async SetMinLevelAsync(device: IDevice, channel: number, level: MinLightLevel): Promise<Status> {
        try {
            const request = new DimmerSettingsParameters();
            request.DeviceGuid = device.Guid;
            request.Channel = channel;
            request.State = level;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetMinLightLevelRequest(request), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set mimimum light level - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set mimimum light level, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            return Status.FatalError;
        }
    }

    async GetMaxLevelAsync(device: IDevice, channel: number): Promise<ResponseResult<SwitchConfigurationErrorCode> | MaxLightLevel> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.InvalidChannelNumber, `Cannot get maximum light level - invalid channel number, channel: ${channel}`);

            const result = await this._connection?.SendAndWaitForResponseAsync<DimmerSettingsParameters>(new GetMaxLightLevelRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get maximum light level - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get maximum light level - controller responded with error code: ${result.Status}.`);

            if(result.Data == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.NoData, `Cannot get maximum light level - no data in response.`);

            return result.Data.State as MaxLightLevel;
        } catch (error) {
            return new ResponseResult(SwitchConfigurationErrorCode.OtherError, `Cannot get maximum light level - unknown error.`);
        }
    }

    async SetMaxLevelAsync(device: IDevice, channel: number, level: MaxLightLevel): Promise<Status> {
        try {
            const request = new DimmerSettingsParameters();
            request.DeviceGuid = device.Guid;
            request.Channel = channel;
            request.State = level;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetMaxLightLevelRequest(request), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set maximum light level - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set maximum light level, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            return Status.FatalError;
        }
    }
}

class DimmerSettingsParameters {
    Channel: number = 0;
    DeviceGuid: string = "";
    State: number = 0;
    StateOptional: number = 0;
}

//Input/output
class InputOutputDimmerParametersFrame extends InputOutputDimmerParameters {
    DeviceGuid: string = "";
}

class GetDimmerInputOutputParamRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/parameters/input/output";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class SetDimmerInputOutputParamRequest extends DataFrame<InputOutputDimmerParametersFrame> {
    constructor(data: InputOutputDimmerParametersFrame) {
        super();
        this.Resource = "/extalife/device/parameters/input/output";
        this.Method = Method.Put;
        this.Data = data;
    }
}

//BrighteningTime
class GetBrighteningTimeRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/parameters/brightening/time";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class SetBrighteningTimeRequest extends DataFrame<DimmerSettingsParameters> {
    constructor(data: DimmerSettingsParameters) {
        super();
        this.Resource = "/extalife/device/parameters/brightening/time";
        this.Method = Method.Put;
        this.Data = data;
    }
}

//DimmingTimeAsync
class GetDimmingTimeRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/parameters/dimming/time";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class SetDimmingTimeRequest extends DataFrame<DimmerSettingsParameters> {
    constructor(data: DimmerSettingsParameters) {
        super();
        this.Resource = "/extalife/device/parameters/dimming/time";
        this.Method = Method.Put;
        this.Data = data;
    }
}

//MinLightLevel
class GetMinLightLevelRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/parameters/min/level";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class SetMinLightLevelRequest extends DataFrame<DimmerSettingsParameters> {
    constructor(data: DimmerSettingsParameters) {
        super();
        this.Resource = "/extalife/device/parameters/min/level";
        this.Method = Method.Put;
        this.Data = data;
    }
}

//MaxLightLevel
class GetMaxLightLevelRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/parameters/max/level";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class SetMaxLightLevelRequest extends DataFrame<DimmerSettingsParameters> {
    constructor(data: DimmerSettingsParameters) {
        super();
        this.Resource = "/extalife/device/parameters/max/level";
        this.Method = Method.Put;
        this.Data = data;
    }
}